import { FRAMEWORK } from "@/constants/testIds";

const LEVELS = [
  {
    level: 1, publicLabel: "Everyday Bias", technical: "Bias Perception",
    desc: "Silent stereotypes and assumptions someone carries before any interaction happens. Not visible, not actionable — but it shapes everything after it.",
    examples: ["Assuming someone's intelligence or intent based on appearance", "'Us vs. them' framing in someone's head", "Distrust based on identity, before anyone has done anything"],
  },
  {
    level: 2, publicLabel: "Disrespect & Dismissal", technical: "Bias Expression",
    desc: "Bias becomes visible — through tone, words, or attitude. This is the voting feed's entry point.",
    examples: ["Microaggressions and dismissive tone", "Unequal patience or respect", "Hostile or exclusionary body language"],
  },
  {
    level: 3, publicLabel: "Unfair Treatment / Threats & Violence", technical: "Bias Behavior",
    desc: "The most important level. Bias starts affecting real outcomes. This is the fork: Path A is unfair treatment (goes to the voting feed). Path B is aggression or violence (routes straight to BERN, our emergency response system).",
    examples: ["Path A: denial of service, exclusion, selective enforcement", "Path B: confrontational harassment, threats, physical intimidation, assault"],
    highlight: true,
  },
  {
    level: 4, publicLabel: "Unfair Decisions", technical: "Bias Decisions",
    desc: "Bias becomes formal and official — a decision gets made, and it wasn't fair. This is the level our advocacy work targets most directly: pre-employment bias screening exists to catch this before it happens, especially for roles that deal with the public.",
    examples: ["Hiring or firing disparities", "Housing decisions", "Medical treatment differences", "School discipline disparities"],
  },
  {
    level: 5, publicLabel: "Broken Systems", technical: "Institutional Bias",
    desc: "Policies inside one organization that keep producing unequal outcomes — with no accountability.",
    examples: ["Policies that produce unequal outcomes", "Lack of accountability", "Biased practices that became 'just how things are'"],
  },
  {
    level: 6, publicLabel: "Systemic Patterns", technical: "Structural/Systemic Bias",
    desc: "Bias reinforced across multiple institutions at once — the biggest, hardest-to-see scale.",
    examples: ["Intergenerational disparities", "Cross-sector inequality (housing, education, justice, healthcare)", "Persistent gaps by group"],
  },
  {
    level: 7, publicLabel: "BBAP's Response System", technical: "Response & Prevention",
    desc: "This is BBAP itself: documenting behavior, identifying escalation patterns, mapping high-risk contexts, advocating for structural fixes like pre-employment bias screening, and enabling early intervention — ideally catching things before Level 3 turns into real harm.",
    examples: ["Documentation archive", "Pattern & escalation tracking", "Advocacy & lobbying for hiring reform", "Guidelines & SOP library", "BERN emergency response"],
  },
];

export default function AboutFramework() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">The Foundation</p>
      <h1 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight mb-4">The Bias Escalation Framework</h1>
      <p className="text-zinc-600 leading-relaxed mb-4 max-w-2xl">
        Bias doesn't start as violence. It starts as a private thought — and moves, step by step, toward real
        harm if nothing interrupts it. This framework is the backbone of everything BBAP does: how we classify
        scenarios, route emergencies, and track patterns.
      </p>
      <p className="text-zinc-950 font-semibold mb-12">
        Core principle: Bias is behavior. Behavior can be documented. Documented behavior becomes data. Data
        becomes accountability. Accountability becomes change.
      </p>

      <div className="space-y-5">
        {LEVELS.map((l) => (
          <div
            key={l.level}
            data-testid={FRAMEWORK.levelCard(l.level)}
            className={`rounded-sm p-6 border transition-all ${l.highlight ? "bg-[#4FD1C5]/5 border-[#1E3A6E]/40" : "bg-white border-zinc-200"}`}
          >
            <div className="flex items-start gap-4">
              <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-display font-bold ${l.highlight ? "bg-zinc-950 text-white" : "border border-zinc-300 text-zinc-500"}`}>
                {l.level}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Level {l.level} · {l.technical}</p>
                <h3 className="font-display font-bold text-lg text-zinc-950 mb-2">{l.publicLabel}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed mb-3">{l.desc}</p>
                <ul className="space-y-1">
                  {l.examples.map((ex) => (
                    <li key={ex} className="text-zinc-500 text-sm flex gap-2">
                      <span className="text-[#1E3A6E]">—</span> {ex}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
